import { makeStyles } from '@material-ui/core';
import { Grid, Typography } from '@material-ui/core/';
import React, { useState, useMemo } from 'react';
import {
  FormDataConsumer,
  ReferenceInput,
  required,
  SelectInput,
  TextInput,
  Loading,
  useQuery
} from 'react-admin';
import { Field } from 'react-final-form';
import Confirm from '../../core/components/forked/layout/Confirm';
import DocumentCategoryApi from '../../core/apis/DocumentCategoryApi';
import CheckBoxField from '../../core/components/CheckBoxField';
import {
  categoryStatus,
  automatedNotificationsOptions
} from './CategoryLookups';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import LightTooltip from '../../core/components/LightTooltip';
import { FormSpy } from 'react-final-form';
import get from 'lodash/get';
import { fieldLengthValidator } from '../../core/utils/index';

const useStyles = makeStyles({
  headerContainer: {
    paddingTop: '0px !important'
  },
  formCheckBox: {
    marginTop: '25px'
  },
  freezeComponentStyle: {
    display: 'flex'
  },
  main: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: 'auto'
  },
  labelStyle: {
    flexGrow: 1,
    maxWidth: '100%'
  },
  infoIcon: {
    color: '#757575',
    paddingTop: '32px',
    paddingLeft: '6px'
  }
});

const CategorySummary = ({ record, ...props }) => {
  const [formErrors, setFormErrors] = useState({});
  const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false);
  const classes = useStyles();

  const {
    data: tenantConfigurations,
    loading: loadingTenantConfigurations
  } = useQuery({
    type: 'getList',
    resource: 'tenantconfigurations',
    payload: {
      filter: {
        nameOrCategory: 'YN_freezeInvestors'
      }
    }
  });

  const isInvestorFreezeOn = useMemo(() => {
    return get(tenantConfigurations, '[0].value', 'N') === 'Y';
  }, [tenantConfigurations]);

  if (loadingTenantConfigurations) return <Loading />;

  const handleConfirmDialogConfirm = () => {
    setConfirmDialogIsOpen(false);
  };

  /*
   * Verify that Category Name is unique
   */
  const validateCategoryName = async (value, allValues, props) => {
    if (record && value === record.name) {
      return;
    }

    if (props.active) {
      const nameValid = !!value.trim();
      let error;

      if (!nameValid) {
        error = `Category Name can't be empty space(s)`;
      } else {
        const nameExists = await DocumentCategoryApi.checkNameExists(value);
        if (nameExists) {
          error = 'Document category with this name already exists';
        } else {
          error = undefined;
        }
      }
      setFormErrors({ ...formErrors, name: error });
      return error;
    }
    return formErrors.name;
  };

  const validateTriggerEmailOption = async value => {
    const isValid = automatedNotificationsOptions.some(
      option => option.id === String(value)
    );
    return isValid ? undefined : 'Required';
  };

  return (
    <FormSpy>
      {formProps => {
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} className={classes.headerContainer}>
              <Typography variant="h5">Category Details</Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={4}>
              <TextInput
                source="name"
                label="Category Name"
                validate={[
                  required(),
                  fieldLengthValidator(128),
                  validateCategoryName
                ]}
                options={{ autoComplete: 'off' }}
                variant={props.variant}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={4}>
              <SelectInput
                source="triggerEmail"
                label="Automated Notifications"
                choices={automatedNotificationsOptions}
                validate={[validateTriggerEmailOption, required()]}
                variant="standard"
              />
            </Grid>
            {isInvestorFreezeOn ? (
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={4}
                className={classes.freezeComponentStyle}
              >
                <Field
                  name="fundLevelFreeze"
                  component={CheckBoxField}
                  label="Enable Investor Freeze"
                  variant={props.variant}
                  type="checkbox"
                  classesOverride={classes}
                />
                <LightTooltip
                  placement="bottom"
                  title="Limit investor’s access to fund level documents for a specific time period"
                >
                  <InfoOutlinedIcon className={classes.infoIcon} />
                </LightTooltip>
              </Grid>
            ) : (
              <Grid item xs={12} sm={12} md={6} lg={4} />
            )}
            <Grid item xs={12} sm={12} md={6} lg={4}>
              <TextInput
                multiline
                source="description"
                label="Category Description"
                validate={[fieldLengthValidator(1000)]}
                rows="3"
                options={{ autoComplete: 'off' }}
                variant={props.variant}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={4}>
              <FormDataConsumer>
                {({ formData, ...rest }) =>
                  formData && (
                    <ReferenceInput
                      label="Email Notification Template"
                      source="templateId"
                      resource="mailtemplatesonlyinfo"
                      reference="mailtemplatesonlyinfo"
                      disabled={!formData.triggerEmail}
                      variant={props.variant}
                      allowEmpty
                      emptyValue={0}
                      perPage={-1}
                    >
                      <SelectInput optionText="description" />
                    </ReferenceInput>
                  )
                }
              </FormDataConsumer>
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              className={classes.formCheckBox}
            >
              <Field
                name="excludeFromDailySummary"
                component={CheckBoxField}
                label="Exclude From Daily Summary Email"
                variant={props.variant}
                type="checkbox"
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={4}>
              <SelectInput
                source="status"
                label="Category Status"
                choices={categoryStatus}
                validate={[required()]}
                variant={props.variant}
                onChange={e => {
                  if (
                    !props.creation &&
                    e.target.value === 'SUSPENDED' &&
                    formProps.values.status === 'ACTIVE'
                  ) {
                    setConfirmDialogIsOpen(true);
                  }
                }}
              />
              <Confirm
                isOpen={confirmDialogIsOpen}
                title="Confirm Suspend"
                content={
                  <div>
                    <p style={{ margin: 0 }}>
                      Are you sure you want to suspend this category?
                    </p>
                    <p>
                      All documents loaded to this category will no longer be
                      visible to any user.
                    </p>
                  </div>
                }
                onConfirm={handleConfirmDialogConfirm}
                onClose={() => {
                  formProps.values.status = 'ACTIVE';
                  setConfirmDialogIsOpen(false);
                }}
                confirm="ra.action.confirm"
              />
            </Grid>
          </Grid>
        );
      }}
    </FormSpy>
  );
};

export default CategorySummary;
